import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import checkIcon from '@images/home/icon-check.svg';
import xIcon from '@images/home/icon-close.svg';

import { HOME_COMPARISON_ROWS, HOME_COMPARISON_ROWS_MOBILE } from './data';
import { EntranceHeadline } from './entrance_headline';
import { HomeQuoteCta } from './quote_cta';
import { BodyMd, HeadingH2, Section, SectionContainer } from './styles';
import { MEDIA, TOKENS } from './tokens';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[48]};

  ${MEDIA.lg} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${TOKENS.space[80]};
  }
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[32]};
  width: 100%;
  max-width: none;
  flex-shrink: 0;
  padding-top: 0;

  ${MEDIA.lg} {
    width: 360px;
    max-width: 360px;
    padding-top: ${TOKENS.space[8]};
  }
`;

const IntroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
`;

const ComparisonBody = styled(BodyMd)`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;

  ${MEDIA.lg} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const tableBackground = `color-mix(
  in oklab,
  ${TOKENS.color.backgroundMuted} 50%,
  ${TOKENS.color.background} 50%
)`;

const TableWrap = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: ${TOKENS.radius.md};
  background: ${tableBackground};

  ${MEDIA.lg} {
    flex: 1 1 832px;
    width: 832px;
    max-width: 832px;
  }
`;

const tableGrid = `
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(72px, 0.75fr) minmax(72px, 0.75fr);
  align-items: center;

  ${MEDIA.md} {
    grid-template-columns: minmax(0, 1.5fr) minmax(96px, 0.8fr) minmax(96px, 0.8fr);
  }

  ${MEDIA.lg} {
    grid-template-columns: minmax(0, 1.45fr) 160px 200px;
  }
`;

const tableCellPadding = css`
  padding: 20px 16px;

  ${MEDIA.lg} {
    padding: 28px 32px;
  }
`;

const TableHeader = styled.div`
  ${tableGrid}
  ${tableCellPadding}
  border-bottom: 1px solid ${TOKENS.color.backgroundMuted};
`;

const TableBody = styled.div``;

const TableRow = styled.div<{ $bordered?: boolean }>`
  ${tableGrid}
  ${tableCellPadding}
  border-top: ${({ $bordered }) =>
    $bordered ? `1px solid ${TOKENS.color.backgroundMuted}` : 'none'};
`;

const HeaderLabel = styled.span<{ $align?: 'start' | 'center' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align = 'center' }) =>
    $align === 'start' ? 'flex-start' : 'center'};
  text-align: ${({ $align = 'center' }) => $align};
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  font-weight: 500;
  color: ${TOKENS.color.foreground};

  ${MEDIA.lg} {
    font-size: 20px;
    line-height: 1.3;
    font-weight: 500;
  }
`;

const FeatureLabel = styled.span`
  text-align: left;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 1.4;
  color: ${TOKENS.color.foreground};

  ${MEDIA.lg} {
    font-size: 16px;
    line-height: 1.5;
  }
`;

const IconCell = styled.div`
  display: flex;
  justify-content: center;

  img {
    width: 20px;
    height: 20px;

    ${MEDIA.lg} {
      width: 30px;
      height: 30px;
    }
  }
`;

const ComparisonQuoteCta = styled(HomeQuoteCta)`
  width: 100%;
  justify-content: center;

  ${MEDIA.lg} {
    width: fit-content;
  }
`;

const DesktopOnly = styled.div`
  display: none;

  ${MEDIA.lg} {
    display: block;
  }
`;

const MobileOnly = styled.div`
  display: block;

  ${MEDIA.lg} {
    display: none;
  }
`;

const IntroCta = styled(ComparisonQuoteCta)`
  display: none;

  ${MEDIA.lg} {
    display: inline-flex;
  }
`;

const MobileCta = styled(ComparisonQuoteCta)`
  && {
    display: inline-flex;
    box-sizing: border-box;
    width: 169px;
    max-width: 169px;
    min-height: 48px;
    height: 48px;
    margin-top: ${TOKENS.space[24]};
    padding: 0 ${TOKENS.space[16]};
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }
`;

const ComparisonTable: React.FC<{
  rows: typeof HOME_COMPARISON_ROWS;
  traditionalLabel: string;
}> = ({ rows, traditionalLabel }) => (
  <>
    <TableHeader>
      <HeaderLabel $align="start">Features</HeaderLabel>
      <HeaderLabel>Clutter</HeaderLabel>
      <HeaderLabel>{traditionalLabel}</HeaderLabel>
    </TableHeader>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow key={row.feature} $bordered={index > 0}>
          <FeatureLabel>{row.feature}</FeatureLabel>
          <IconCell>
            <img
              src={checkIcon}
              alt={`Clutter includes ${row.feature.toLowerCase()}`}
            />
          </IconCell>
          <IconCell>
            <img
              src={xIcon}
              alt={`Traditional storage does not include ${row.feature.toLowerCase()}`}
            />
          </IconCell>
        </TableRow>
      ))}
    </TableBody>
  </>
);

export const HomeComparisonSection: React.FC = () => (
  <Section id="comparison" $variant="comparison">
    <SectionContainer>
      <Layout>
        <Intro>
          <IntroCopy>
            <EntranceHeadline as={HeadingH2}>
              Clutter vs. traditional
              <br />
              storage solutions
            </EntranceHeadline>
            <ComparisonBody $secondary>
              Most traditional storage services put the work on you. We do it
              instead.
            </ComparisonBody>
          </IntroCopy>
          <IntroCta label="Get a free quote" />
        </Intro>

        <div>
          <TableWrap>
            <MobileOnly>
              <ComparisonTable
                rows={HOME_COMPARISON_ROWS_MOBILE}
                traditionalLabel="Traditional"
              />
            </MobileOnly>
            <DesktopOnly>
              <ComparisonTable
                rows={HOME_COMPARISON_ROWS}
                traditionalLabel="Traditional self-storage"
              />
            </DesktopOnly>
          </TableWrap>
          <MobileOnly>
            <MobileCta label="Get a free quote" />
          </MobileOnly>
        </div>
      </Layout>
    </SectionContainer>
  </Section>
);
